import axios from 'axios';
// Storage keys
const ACCESS_TOKEN_KEY = 'fs_access_token';
const REFRESH_TOKEN_KEY = 'fs_refresh_token';
const REMEMBER_ME_KEY = 'fs_remember_me';
// Public paths that should NOT redirect to login on auth failure
const PUBLIC_PATHS = [
    '/',
    '/ab-test',
    '/login',
    '/signup',
    '/landing',
    '/verify-email',
    '/forgot-password',
    '/reset-password',
    '/terms',
    '/privacy',
    '/unauthorized',
    '/trial-expired',
    '/checkout',
    '/invitations',
    '/accept-invite',
];
/**
 * Check if current path is a public page that shouldn't redirect on auth failure
 */
const isPublicPath = () => {
    if (typeof window === 'undefined')
        return false;
    const path = window.location.pathname;
    return PUBLIC_PATHS.some((publicPath) => {
        // Exact match for root path
        if (publicPath === '/')
            return path === '/';
        // Prefix match for other paths
        return path === publicPath || path.startsWith(publicPath + '/');
    });
};
// Token storage utilities
// When "Remember Me" is checked, tokens persist in localStorage (survive tab close).
// When unchecked, tokens go to sessionStorage (cleared when tab closes).
// The preference itself is always in localStorage so we know which store to check on reload.
function getStore() {
    if (typeof window === 'undefined')
        return localStorage;
    return localStorage.getItem(REMEMBER_ME_KEY) === '0' ? sessionStorage : localStorage;
}
export const tokenStorage = {
    getAccessToken: () => {
        if (typeof window === 'undefined')
            return null;
        return getStore().getItem(ACCESS_TOKEN_KEY);
    },
    getRefreshToken: () => {
        if (typeof window === 'undefined')
            return null;
        return getStore().getItem(REFRESH_TOKEN_KEY);
    },
    setTokens: (accessToken, refreshToken) => {
        if (typeof window === 'undefined')
            return;
        const store = getStore();
        store.setItem(ACCESS_TOKEN_KEY, accessToken);
        store.setItem(REFRESH_TOKEN_KEY, refreshToken);
    },
    clearTokens: () => {
        if (typeof window === 'undefined')
            return;
        // Clear from both stores to ensure no stale tokens
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    },
    setRememberMe: (remember) => {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem(REMEMBER_ME_KEY, remember ? '1' : '0');
    },
    getRememberMe: () => {
        if (typeof window === 'undefined')
            return true;
        return localStorage.getItem(REMEMBER_ME_KEY) !== '0';
    },
};
// Get API base URL from environment
const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
        // Browser environment - check for Vite env variable
        return (window.env?.VITE_API_URL ||
            import.meta.env?.VITE_API_URL ||
            'http://localhost:3001');
    }
    return 'http://localhost:3001';
};
// Deduplicate concurrent token refresh calls
let refreshPromise = null;
// Cloud Run cold-start retry config. 5xx from the Google LB or a dropped
// connection lands in the browser as a CORS/network error because the LB
// skips CORS headers on its own error pages. Retry a few times with
// exponential backoff so a single cold-start blip doesn't bounce the user
// to /login.
const RETRIABLE_STATUS = new Set([502, 503, 504]);
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 400;
const JITTER = 0.2;
function retryDelay(attempt) {
    const exp = BASE_DELAY_MS * 2 ** (attempt - 1);
    const jitter = exp * JITTER * (Math.random() * 2 - 1);
    return Math.max(0, Math.round(exp + jitter));
}
function isRetriable(error) {
    if (!axios.isAxiosError(error))
        return false;
    // Don't retry user-cancelled requests.
    if (error.code === 'ERR_CANCELED')
        return false;
    const status = error.response?.status;
    if (status && RETRIABLE_STATUS.has(status))
        return true;
    // No response object = network error / CORS-on-5xx / timeout.
    return !error.response;
}
// Refresh the access token with the same retry policy as regular requests.
// Uses a bare axios.post (not the client instance) so we don't recurse into
// the 401 interceptor if the refresh itself returns 401.
async function refreshTokens(refreshToken) {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
            const res = await axios.post(`${getBaseUrl()}/api/auth/refresh`, { refreshToken });
            return res.data.success && res.data.data ? res.data.data : null;
        }
        catch (err) {
            if (attempt < MAX_RETRIES && isRetriable(err)) {
                await new Promise((resolve) => setTimeout(resolve, retryDelay(attempt + 1)));
                continue;
            }
            throw err;
        }
    }
    return null;
}
// Create axios instance
const createApiClient = () => {
    const client = axios.create({
        baseURL: getBaseUrl(),
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 seconds
    });
    // Request interceptor - attach JWT
    client.interceptors.request.use((config) => {
        const token = tokenStorage.getAccessToken();
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    }, (error) => {
        return Promise.reject(error);
    });
    // Response interceptor - retry transient 5xx, then handle 401
    client.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error.config;
        if (originalRequest && isRetriable(error)) {
            const attempt = (originalRequest._retryCount ?? 0) + 1;
            if (attempt <= MAX_RETRIES) {
                originalRequest._retryCount = attempt;
                await new Promise((resolve) => setTimeout(resolve, retryDelay(attempt)));
                return client(originalRequest);
            }
        }
        if (!originalRequest) {
            return Promise.reject(error);
        }
        // If 401 and we haven't retried yet, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = tokenStorage.getRefreshToken();
            if (refreshToken) {
                try {
                    // Deduplicate: if a refresh is already in flight, wait for it
                    if (!refreshPromise) {
                        refreshPromise = refreshTokens(refreshToken).finally(() => {
                            refreshPromise = null;
                        });
                    }
                    const tokens = await refreshPromise;
                    if (tokens) {
                        tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken);
                        // Retry original request with new token
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
                        }
                        return client(originalRequest);
                    }
                }
                catch {
                    refreshPromise = null;
                    // Refresh failed, clear tokens
                    tokenStorage.clearTokens();
                    // Only redirect to login if NOT on a public page
                    if (typeof window !== 'undefined' && !isPublicPath()) {
                        window.location.href = '/login';
                    }
                    return Promise.reject(error);
                }
            }
            // No refresh token, clear tokens
            tokenStorage.clearTokens();
            // Only redirect to login if NOT on a public page
            if (typeof window !== 'undefined' && !isPublicPath()) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    });
    return client;
};
// Singleton instance
export const apiClient = createApiClient();
/**
 * Get the API base URL for constructing direct URLs (e.g., for images)
 */
export function getApiBaseUrl() {
    return getBaseUrl();
}
// ============================================
// TYPED REQUEST FUNCTIONS
// ============================================
/**
 * GET request with typed response
 */
export async function get(url, config) {
    const response = await apiClient.get(url, config);
    return response.data;
}
/**
 * POST request with typed response
 */
export async function post(url, data, config) {
    const response = await apiClient.post(url, data, config);
    return response.data;
}
/**
 * PATCH request with typed response
 */
export async function patch(url, data, config) {
    const response = await apiClient.patch(url, data, config);
    return response.data;
}
/**
 * PUT request with typed response
 */
export async function put(url, data, config) {
    const response = await apiClient.put(url, data, config);
    return response.data;
}
/**
 * DELETE request with typed response
 */
export async function del(url, config) {
    const response = await apiClient.delete(url, config);
    return response.data;
}
/**
 * Extract error message from axios error
 */
export function getErrorMessage(error) {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        if (data?.error) {
            return data.error;
        }
        if (error.message) {
            return error.message;
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An unexpected error occurred';
}
/**
 * Extract error code from axios error
 */
export function getErrorCode(error) {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        return data?.code;
    }
    return undefined;
}
/**
 * Check if error is a specific HTTP status
 */
export function isHttpError(error, status) {
    if (axios.isAxiosError(error)) {
        return error.response?.status === status;
    }
    return false;
}
/**
 * Check if error is a network error
 */
export function isNetworkError(error) {
    if (axios.isAxiosError(error)) {
        return !error.response && error.code === 'ERR_NETWORK';
    }
    return false;
}
//# sourceMappingURL=apiClient.js.map